import React from 'react';
import { Loader, BaasicTable } from 'core/components';
import { defaultTemplate } from 'core/hoc';
import { formatDate } from 'core/utils';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import _ from 'lodash';

// Kendo renders the detail component with only dataItem, so the store reaches it through context.
const InventoryStoreContext = React.createContext(null);

const ProductInventoryListTemplate = props => {
	const { productDetailViewStore, actions, t } = props;
	const {
		loading,
		listStore,
		tableData,
		queryUtility,
		noDataItem,
		editField,
		edit,
		remove,
		cancel,
		update,
		product,
		updateTable, // DO NOT DELETE! Used for re-render.
		productInventory,
		onItemChange,
		openDescriptionModal,
		onCancelBatch,
		allowEdit,
		addRow,
		confirmDisabled,
		cancelDisabled,
		organizationsCount,
		isSeller,
		isSuperAdminOrganization,
		removeRow,
		onInventoryLogPerWarehouse,
		showCustomActions,
		viewStore,
		onSyncWithWmsPerWarehouse,
		onSyncInventoryForAllWarehouses,
		warehouseSyncStatus,
		onInventoryExpandChange,
	} = productDetailViewStore;
	if (loading) return <Loader />;
	return (
		<section>
			{(isSeller || isSuperAdminOrganization) && (
				<div className="flex flex--secondary spc--top--sml spc--left--sml spc--right--sml">
					{productInventory.length < organizationsCount && (isSeller || isSuperAdminOrganization) && (
						<button
							className="btn btn--med btn--primary spc--bottom--sml"
							disabled={product.isMultiProduct}
							onClick={() => addRow()}
						>
							<i className="fa fa-plus spc--right--tny align--v--middle"></i>
							{t('ADDINVENTORYDATA')}
						</button>
					)}
					{productInventory.length != 0 && (
						<div className="align--h--right">
							<button
								className="btn btn--med btn--primary spc--right--sml spc--bottom--sml"
								onClick={openDescriptionModal}
								disabled={confirmDisabled}
							>
								{t('CONFIRM')}
							</button>
							<button
								className="btn btn--med btn--ghost spc--bottom--sml"
								onClick={onCancelBatch}
								disabled={cancelDisabled}
							>
								{t('CANCEL')}
							</button>
						</div>
					)}
				</div>
			)}
			<BaasicTable
				listEmptyKey="LISTEMPTY.PRODUCTINVENTORY"
				{...{
					listStore,
					data: productInventory.slice(),
					tableData,
					queryUtility,
					productDetailViewStore,
					actions,
					noDataItem,
					editField: !product.isMultiProduct ? editField : null,
					edit,
					remove,
					cancel,
					update,
					onItemChange,
					allowEdit,
					removeRow,
					onInventoryLogPerWarehouse,
					showCustomActions,
					viewStore,
					onSyncWithWmsPerWarehouse,
					onSyncInventoryForAllWarehouses,
					warehouseSyncStatus,
					detailComponent: InventoryWmsDetail,
				}}
				viewStore={productDetailViewStore}
				rowRender={InventoryTableRowRender}
				expandChange={onInventoryExpandChange}
			/>
		</section>
	);
};

function InventoryTableRowRender(trElement, { dataItem }) {
	// Keep Kendo's own row classes (k-master-row), which the detail row styling relies on.
	const className = [trElement.props.className, dataItem.isNewRow ? 'is-new' : ''].filter(Boolean).join(' ');
	return React.cloneElement(trElement, { className });
}

const InventoryWmsDetail = defaultTemplate(({ dataItem, t }) => {
	const { isSuperAdminOrganization, onItemChange, updateTable } = React.useContext(InventoryStoreContext); // eslint-disable-line no-unused-vars
	const snapshot = dataItem.productInventoryWmsSnapshot;
	const canEditWarehouseOnHold = isSuperAdminOrganization && dataItem.inEdit === true;
	const valueOrDash = value => (value != null ? value : '-');
	return (
		<div className="spc--top--sml spc--bottom--sml">
			<h4 className="order__header__title type--base">{t('PRODUCTDETAILVIEW.INVENTORY.WMSDETAILTITLE')}</h4>
			<div className="row">
				<div className="col col-sml-3">
					<p className="order__label spc--top--sml">{t('PRODUCTDETAILVIEW.INVENTORY.WAREHOUSEAVAILABLEQUANTITY')}</p>
					<p>{valueOrDash(snapshot && snapshot.availableQuantity)}</p>
				</div>
				<div className="col col-sml-3">
					<p className="order__label spc--top--sml">{t('PRODUCTDETAILVIEW.INVENTORY.WAREHOUSEPICKABLEQUANTITY')}</p>
					<p>{valueOrDash(snapshot && snapshot.pickableQuantity)}</p>
				</div>
				<div className="col col-sml-3">
					<p className="order__label spc--top--sml">{t('PRODUCTDETAILVIEW.INVENTORY.WAREHOUSEONHOLDQUANTITY')}</p>
					{canEditWarehouseOnHold ? (
						<NumericTextBox
							value={dataItem.warehouseOnHoldQuantity}
							min={0}
							onChange={e => onItemChange({ dataItem, field: 'warehouseOnHoldQuantity', value: e.value })}
						/>
					) : (
						<p>{valueOrDash(dataItem.warehouseOnHoldQuantity)}</p>
					)}
				</div>
				<div className="col col-sml-3">
					<p className="order__label spc--top--sml">{t('PRODUCTDETAILVIEW.INVENTORY.WMSLASTSYNCED')}</p>
					<p>{snapshot ? formatDate(snapshot.lastSyncedAt, true, 'LLL') : '-'}</p>
				</div>
			</div>
		</div>
	);
});

const ObservedProductInventoryListTemplate = defaultTemplate(ProductInventoryListTemplate);

const ProductInventoryListTemplateWithStore = props => (
	<InventoryStoreContext.Provider value={props.productDetailViewStore}>
		<ObservedProductInventoryListTemplate {...props} />
	</InventoryStoreContext.Provider>
);

export default ProductInventoryListTemplateWithStore;
